import type { ModuleName, Permission, PreviewModule } from "@dentacore/shared-types";

import { SYSTEM_PERMISSIONS } from "../auth/use-can";

/**
 * The sidebar — **Emile's structure, MS-D3–MS-D8 (2026-10-16)**.
 *
 * Home · Tasks (a preview, only where switched on — TK-D36) · Workflows (the
 * same, WF-D6) · Calendar ·
 * **People** (Patients, Staff, Suppliers) · Treatment plans ·
 * **HR** (Shifts, Requests, Staff management, Me) — then Supply's **Products**
 * (Catalogue, Supplier items), **Orders** (Cart, Order history) and Inventory —
 * then Accounting — and System last. Groups are headings with their pages listed
 * under them as their own links (MS-D7), and a divider separates the base plan,
 * Supply and Accounting (MS-D4). Chatbot has no item: it is the WhatsApp queue
 * beside the calendar, reminders and the two bots.
 *
 * **No screen is the "main" one** (CLAUDE.md); Home is first because that is
 * where the day starts, not because it outranks the rest.
 *
 * Icons are one `d` on a 24×24 viewBox at stroke-width 1.6, the idiom the
 * mockup's five set; the newer ones are drawn to match.
 */
export interface NavItem {
  to: string;
  label: string;
  /** A single `d` attribute, drawn on a 24×24 viewBox at stroke-width 1.6. */
  icon: string;
  /**
   * Shown to anyone holding at least one of these (STORY-166). **Presentation
   * only** — every route behind the item checks for itself, and nothing may be
   * built on top of this as though it were the permission check (S3).
   */
  needs?: readonly Permission[];
  /** Hidden from anyone holding this — Me is for whoever cannot see the team (HR-D18). */
  hideFor?: Permission;
  /**
   * The add-on this page belongs to (STORY-188b, MOD-D11). Without it the link
   * shows locked and opens a prompt instead of the page — **unless
   * `hideWhenLocked`**, for an add-on's page that sits inside a base group and
   * simply is not there without it (MS-D5): Suppliers under People.
   */
  module?: ModuleName;
  hideWhenLocked?: boolean;
  /**
   * A preview module this page belongs to (TK-D28): **absent entirely** while
   * the clinic does not have it — no tag, no prompt, since it is not for sale
   * (TK-D36).
   */
  preview?: PreviewModule;
  /** Matches only the exact path, for `/`. */
  end?: boolean;
  testId?: string;
}

export interface NavGroup {
  label: string;
  icon: string;
  /** The add-on the whole group belongs to; its links inherit it. */
  module?: ModuleName;
  items: readonly NavItem[];
  testId: string;
}

/** A thin rule between the base plan, Supply and Accounting (MS-D4). */
export interface NavDivider {
  divider: true;
}

export type NavEntry = NavItem | NavGroup | NavDivider;

export const isGroup = (entry: NavEntry): entry is NavGroup => "items" in entry;
export const isDivider = (entry: NavEntry): entry is NavDivider => "divider" in entry;

export const NAV: readonly NavEntry[] = [
  {
    to: "/",
    label: "Home",
    end: true,
    needs: ["home.view"],
    icon: "M3 10.75 12 3l9 7.75V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z",
    testId: "nav-home",
  },
  // Right after Home (TK-D36): something people check daily, like Home.
  {
    to: "/tasks",
    label: "Tasks",
    preview: "TASKS",
    icon: "M10 6h10M10 12h10M10 18h10M4 6l1.2 1.2L7.5 5M4 12l1.2 1.2L7.5 11M4 18l1.2 1.2L7.5 17",
    testId: "nav-tasks",
  },
  // Right after Tasks (WF-D6), a preview the same way (WG-D15).
  {
    to: "/workflows",
    label: "Workflows",
    preview: "WORKFLOWS",
    // Three boxes joined by arrows, the middle one forking down.
    icon: "M3 9h5v5H3zM16 4h5v5h-5zM16 15h5v5h-5zM8 11.5h3.5V6.5H16M11.5 11.5v6H16",
    testId: "nav-workflows",
  },
  {
    to: "/calendar",
    label: "Calendar",
    needs: ["calendar.view"],
    icon: "M4 6h16v15H4zM4 10.5h16M8.5 3v4M15.5 3v4",
    testId: "nav-calendar",
  },
  {
    label: "People",
    testId: "nav-group-people",
    // Two figures, the nearer one larger.
    icon: "M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6M3.5 19c0-3 2.4-5 5.5-5s5.5 2 5.5 5M16 11a2.5 2.5 0 1 0 0-5M17.5 14c1.9.4 3 1.9 3 4",
    items: [
      {
        to: "/patients",
        label: "Patients",
        needs: ["patients.view"],
        icon: "M12 11.5a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2M4.8 20c0-3.6 3.2-6.2 7.2-6.2s7.2 2.6 7.2 6.2",
        testId: "nav-patients",
      },
      {
        // Who works here — moved out of System → People (MS-D3).
        to: "/people/staff",
        label: "Staff",
        needs: ["people.manage", "access.assign"],
        // A card with a face on it and two lines of detail.
        icon: "M4 6h16v13H4zM8.5 11a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6M6 16c0-1.7 1.2-2.8 2.5-2.8s2.5 1.1 2.5 2.8M14 9.5h4M14 13h4",
        testId: "nav-people-staff",
      },
      {
        // Supply's, inside a base group, so absent rather than locked (MS-D5).
        to: "/people/suppliers",
        label: "Suppliers",
        needs: ["inventory.view"],
        module: "SUPPLY",
        hideWhenLocked: true,
        // A delivery truck.
        icon: "M3 7h11v9H3zM14 10h4l3 3v3h-7zM7 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M17 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3",
        testId: "nav-people-suppliers",
      },
    ],
  },
  {
    to: "/plans",
    label: "Treatment plans",
    needs: ["procedures.view"],
    icon: "M9 4.5h6v3H9zM7 6H5.2v15h13.6V6H17M9 12.5h6M9 16.5h4",
    testId: "nav-plans",
  },
  {
    label: "HR",
    testId: "nav-group-hr",
    // The briefcase Staff carried.
    icon: "M3.5 8h17v12.5h-17zM9 8V4.8h6V8M3.5 13.5h17",
    items: [
      {
        to: "/hr/shifts",
        label: "Shifts",
        needs: ["staff.view", "staff.timeoff.request"],
        icon: "M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17M12 7.5V12l3 2",
        testId: "nav-hr-shifts",
      },
      {
        // Whoever decides leave or sees everyone's (HR-D18, the old Time off tab's rule).
        to: "/hr/requests",
        label: "Requests",
        needs: ["hr.team.view", "staff.timeoff.decide", "staff.leave.view.any"],
        icon: "M4 6h16v14H4zM4 10h16M8.5 3.5v4M15.5 3.5v4M9 15l2 2 4-4",
        testId: "nav-hr-requests",
      },
      {
        // Team and Compliance (HR-D1).
        to: "/hr/staff",
        label: "Staff management",
        needs: ["hr.team.view", "hr.compliance.view"],
        icon: "M9 4.5h6v3H9zM7 6H5.2v15h13.6V6H17M9 12h6M9 16h6",
        testId: "nav-hr-staff",
      },
      {
        // One's own record, for whoever cannot see the team's (HR-D18).
        to: "/hr/me",
        label: "Me",
        // Their own record is `hr.self`'s, which `/hr/me` asks for (QA third run).
        needs: ["hr.self"],
        hideFor: "hr.team.view",
        icon: "M12 12a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M6.5 19.5c.6-2.9 2.8-4.6 5.5-4.6s4.9 1.7 5.5 4.6M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18",
        testId: "nav-hr-me",
      },
    ],
  },
  { divider: true },
  {
    label: "Products",
    testId: "nav-group-products",
    module: "SUPPLY",
    // A box with a lid line and a centre seam — Supply's own.
    icon: "M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5zM3.5 7.5 12 12l8.5-4.5M12 12v9",
    items: [
      {
        to: "/supply/catalogue",
        label: "Catalog",
        needs: ["inventory.view"],
        icon: "M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01",
        testId: "nav-products-catalogue",
      },
      {
        to: "/supply/items",
        label: "Supplier items",
        needs: ["inventory.view"],
        // A price tag.
        icon: "M3.5 12.5V4.5h8l9 9-8 8zM8 8.5h.01",
        testId: "nav-products-items",
      },
    ],
  },
  {
    label: "Orders",
    testId: "nav-group-orders",
    module: "SUPPLY",
    icon: "M3 4h2.5l2 11h11l2-8H7M9.5 19.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2M17 19.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2",
    items: [
      {
        to: "/orders/cart",
        label: "Cart",
        needs: ["inventory.view"],
        icon: "M3 4h2.5l2 11h11l2-8H7M9.5 19.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2M17 19.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2",
        testId: "nav-orders-cart",
      },
      {
        to: "/orders/history",
        label: "Order history",
        needs: ["inventory.view"],
        // A receipt with a torn foot.
        icon: "M6 3.5h12v17l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6",
        testId: "nav-orders-history",
      },
    ],
  },
  {
    to: "/inventory",
    label: "Inventory",
    needs: ["inventory.view"],
    // All stock is Supply (MS-D2).
    module: "SUPPLY",
    // A shelf carrying three tubs.
    icon: "M3.5 9.5h17M6 9.5V6.2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v3.3M5.5 9.5v8.3a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5M9.5 13h5",
    testId: "nav-inventory",
  },
  { divider: true },
  {
    to: "/accounting",
    label: "Accounting",
    // STORY-172 (AC-D13, AC-D15); any one of the three parts opens it (QA4-D8).
    needs: ["accounting.view", "accounting.expenses.view", "accounting.salaries.view"],
    module: "ACCOUNTING",
    // A ledger page with three rising bars.
    icon: "M4 4h16v16H4zM8 16.5v-3.5M12 16.5V9.5M16 16.5V7",
    testId: "nav-accounting",
  },
  {
    to: "/settings",
    // "System" since EB1 (2026-09-29) — the label only; the route stays /settings.
    // One link again: its pages are tabs across the top of System (Emile, 2026-10-22: "remove the dropdown and make tabs, just like the patients page").
    label: "System",
    icon: "M10.4 3.2h3.2l.35 2.2a6.9 6.9 0 0 1 1.9 1.1l2.05-.85 1.6 2.8-1.75 1.35a6.9 6.9 0 0 1 0 2.4l1.75 1.35-1.6 2.8-2.05-.85a6.9 6.9 0 0 1-1.9 1.1l-.35 2.2h-3.2l-.35-2.2a6.9 6.9 0 0 1-1.9-1.1l-2.05.85-1.6-2.8 1.75-1.35a6.9 6.9 0 0 1 0-2.4L4.5 8.45l1.6-2.8 2.05.85a6.9 6.9 0 0 1 1.9-1.1zM12 14.7a2.7 2.7 0 1 0 0-5.4 2.7 2.7 0 0 0 0 5.4",
    needs: SYSTEM_PERMISSIONS,
    testId: "nav-system",
  },
];
